import { validateSOPSubmission } from '@/features/offers/helpers/sop.helper'
import { authSelector } from '@/stores/auth/authSlice'
import { setApprovalInvitedUsers, showApprovalRemarkModal, showResubmitOfferModal } from '@/stores/offers/offersSlice'
import { useConfirmationModalManagement } from '@/stores/ui/confirmationModalSlice'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
import { showModal } from '@/stores/ui/modalSlice'
import { findWithIndex } from '@/utils/arrayHelper'
import { MODAL_TYPES } from '@/utils/constants/modalTypes'
import { Button } from '@chakra-ui/react'
import { useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { ApprovalStatus, OfferApprovalType } from '../../../constants'
import { offerActionGroupPolicyType } from '../../../constants/offerEditRights/offerActionEditRights'
import { formatOffer } from '../../../helpers/formatOffer'
import useOffer from '../../../hooks/useOffer'
import useOfferApproval from '../../../hooks/useOfferApproval'
import { selectOfferDetail, selectOfferPermissions } from '../../../slices/offerDetail.slice'
import { APPROVAL_TABLE_TYPE } from '../../helper/approverTableColumn.helper'
import { findApproverSlots, findInviteeSlots } from '../../helper/approverSlotMatching.helper'
import ApproverSlotActionButton from '../ApproverSlotActionButton'

const QuotationButtonGroup = () => {
  const offerPermissions = useSelector(selectOfferPermissions)
  const { adminMode } = useSelector(selectOfferDetail)
  const { user: currentUser } = useSelector(authSelector)
  const dispatch = useDispatch()
  const { clearErrors, getValues, formState } = useFormContext()
  const { open: openConfirmationModal } = useConfirmationModalManagement()

  const { useUpdateOffer } = useOffer()
  const { useSubmitOfferQuotation } = useOfferApproval()
  const updateOffer = useUpdateOffer()
  const submitOfferQuotation = useSubmitOfferQuotation()

  const canSubmitQuotation = offerPermissions[offerActionGroupPolicyType.ACTION_SUBMIT_QUOTATION]?.edit ?? false
  const canResubmitQuotation = offerPermissions[offerActionGroupPolicyType.ACTION_RESUBMIT_QUOTATION]?.edit ?? false
  const canDoQuotationApproval = offerPermissions[offerActionGroupPolicyType.ACTION_QUOTATION_APPROVAL]?.edit ?? false
  const canDoQuotationApprovalInvitation = canDoQuotationApproval

  const canReplyQuotationInvitation =
    offerPermissions[offerActionGroupPolicyType.ACTION_REPLY_QUOTATION_INVITATION]?.edit ?? false

  const approvers = useWatch({ name: 'approvers' })

  const inviteSlots = useMemo(
    () => findApproverSlots(approvers, currentUser?._id, [ApprovalStatus.REQUIRED]),
    [approvers, currentUser?._id]
  )
  const replySlots = useMemo(() => findInviteeSlots(approvers, currentUser?._id), [approvers, currentUser?._id])

  const handleSubmitQuotation = async () => {
    try {
      dispatch(showLoadingOverlay())
      const offer = getValues()
      const offerId = offer._id
      const formattedOffer = formatOffer(offer)
      clearErrors()
      validateSOPSubmission({
        offerCreateDate: new Date(offer.createdAt),
        countryOfInstallation: offer.countryOfInstallation,
        isEQuotation: offer.isEQuotation,
        SOPQuestionAnswers: offer.SOPQuestionAnswers,
      })
      await submitOfferQuotation.mutateAsync({ id: offerId, data: formattedOffer })
    } catch (err) {
      console.log(err)
      toast.error(err.message || 'Failed to submit the quotation')
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  const handleResubmitQuotation = () => {
    const currentOffer = getValues()
    const formattedOffer = formatOffer(currentOffer)
    dispatch(
      showResubmitOfferModal({
        offerStage: 'quotation',
        offerId: currentOffer.id,
        offerDetail: formattedOffer,
      })
    )
  }

  const handleQuotationApprovalModal = async (approvalType) => {
    const currentOffer = getValues()
    const { isDirty, dirtyFields, touchedFields } = formState
    if (isDirty || Object.keys(dirtyFields).length > 0 || Object.keys(touchedFields).length > 0) {
      const { isConfirmed } = await openConfirmationModal({
        title: 'Confirm changes to the offer',
        message: "You've modified the offer, are you sure to save the changes to proceed ? ",
      })
      if (!isConfirmed) return
      try {
        dispatch(showLoadingOverlay())
        const formattedOffer = formatOffer(currentOffer)
        await updateOffer.mutateAsync({ id: currentOffer._id, data: formattedOffer })
      } catch (error) {
        return
      } finally {
        dispatch(hideLoadingOverlay())
      }
    }

    const { index, object: approverDetail } = findWithIndex(
      currentOffer.approvers,
      (detail) =>
        (detail?.user?._id === currentUser._id || detail?.delegationDetail?.delegatee === currentUser._id) &&
        detail?.status === ApprovalStatus.REQUIRED
    )

    dispatch(
      showApprovalRemarkModal({
        approvalType,
        offerId: currentOffer._id,
        remark: approverDetail?.status === ApprovalStatus.REQUIRED ? approverDetail?.remark : '',
        updatePath: `approvers.${index}`,
        approverType: approverDetail?.type,
        approverLevel: approverDetail?.level,
      })
    )
  }
  const handleOpenQuotationReplyModal = async (slot) => {
    const currentOffer = getValues()
    const { isDirty, dirtyFields, touchedFields } = formState
    if (isDirty || Object.keys(dirtyFields).length > 0 || Object.keys(touchedFields).length > 0) {
      const { isConfirmed } = await openConfirmationModal({
        title: 'Confirm changes to the offer',
        message: "You've modified the offer, are you sure to save the changes to proceed ? ",
      })
      if (!isConfirmed) return
      try {
        dispatch(showLoadingOverlay())
        const formattedOffer = formatOffer(currentOffer)
        await updateOffer.mutateAsync({ id: currentOffer._id, data: formattedOffer })
      } catch (error) {
        return
      } finally {
        dispatch(hideLoadingOverlay())
      }
    }

    const { approverIndex, detail: inviterDetail } = slot

    dispatch(setApprovalInvitedUsers(inviterDetail?.invitedUsers || []))
    dispatch(
      showModal({
        modalType: MODAL_TYPES.INVITATION_DETAIL,
        modalProps: {
          updateInvitationParameter: {
            offerId: currentOffer._id,
            approverIndex,
          },
          approvalRole: inviterDetail?.type,
          canManageInvitations: canDoQuotationApprovalInvitation,
          tableType: APPROVAL_TABLE_TYPE.QUOTATION_APPROVAL,
        },
      })
    )
  }

  const handleOpenQuotationInvitationDetailModal = async (slot) => {
    const currentOffer = getValues()
    const { isDirty, dirtyFields, touchedFields } = formState
    if (isDirty || Object.keys(dirtyFields).length > 0 || Object.keys(touchedFields).length > 0) {
      const { isConfirmed } = await openConfirmationModal({
        title: 'Confirm changes to the offer',
        message: "You've modified the offer, are you sure to save the changes to proceed ? ",
      })
      if (!isConfirmed) return
      try {
        dispatch(showLoadingOverlay())
        const formattedOffer = formatOffer(currentOffer)
        await updateOffer.mutateAsync({ id: currentOffer._id, data: formattedOffer })
      } catch (error) {
        return
      } finally {
        dispatch(hideLoadingOverlay())
      }
    }

    const { approverIndex, detail: approverDetail } = slot

    dispatch(setApprovalInvitedUsers(approverDetail?.invitedUsers || []))
    dispatch(
      showModal({
        modalType: MODAL_TYPES.INVITATION_DETAIL,
        modalProps: {
          updateInvitationParameter: {
            offerId: currentOffer._id,
            approverIndex,
          },
          approvalRole: approverDetail?.type,
          canManageInvitations: canDoQuotationApprovalInvitation,
          tableType: APPROVAL_TABLE_TYPE.QUOTATION_APPROVAL,
        },
      })
    )
  }
  return (
    <>
      {canSubmitQuotation && !adminMode && (
        <Button colorScheme="green" onClick={handleSubmitQuotation}>
          Submit
        </Button>
      )}
      {canResubmitQuotation && !adminMode && (
        <Button colorScheme="green" onClick={handleResubmitQuotation}>
          Resubmit
        </Button>
      )}
      {canDoQuotationApproval && !adminMode && (
        <>
          <Button colorScheme="green" onClick={() => handleQuotationApprovalModal(OfferApprovalType.QUOTATION_APPROVE)}>
            Approve
          </Button>
          <Button colorScheme="red" onClick={() => handleQuotationApprovalModal(OfferApprovalType.QUOTATION_REJECT)}>
            Reject
          </Button>
          {canDoQuotationApprovalInvitation && (
            <ApproverSlotActionButton
              label="Invite"
              colorScheme="teal"
              slots={inviteSlots}
              onSelect={handleOpenQuotationInvitationDetailModal}
            />
          )}
        </>
      )}
      {canReplyQuotationInvitation && !adminMode && (
        <ApproverSlotActionButton
          label="Reply Quotation"
          colorScheme="green"
          slots={replySlots}
          onSelect={handleOpenQuotationReplyModal}
        />
      )}
    </>
  )
}

export default QuotationButtonGroup
